import cx from 'classnames';
import T from 'components/common/text/T';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ProjectContext } from 'contexts/project/ProjectContext';
import useInput from 'lib/hooks/useInput';
import ProjectModelViewer from './ProjectModelViewer';
import { useProjectFileStore } from 'store/local/projectFile';
import CustomText from 'components/common/text/CustomText';
import { color } from 'styles/utils';
import { useProjectFiles } from 'lib/hooks/project/useProjectFiles';

interface ProjectFileViewerProps {}

function ProjectFileViewer({}: ProjectFileViewerProps) {
  const [hasViewer, setHasViewer] = useState<boolean>(false);
  const { classifyClinicFiles } = useContext(ProjectContext);
  const isModelFileLoadingProgress = useInput(true);
  const { modelError } = useProjectFileStore();

  const { handleClearAllView } = useProjectFiles();

  const { clinicViewModelFiles, clinicViewImageFiles, clinicViewHtmlFiles } = classifyClinicFiles;

  useEffect(() => {
    if (
      clinicViewModelFiles.value.length < 1 &&
      !clinicViewImageFiles.value &&
      !clinicViewHtmlFiles.value
    ) {
      setHasViewer(false);
    } else {
      setHasViewer(true);
    }
  }, [clinicViewModelFiles.value, clinicViewImageFiles.value, clinicViewHtmlFiles.value]);

  useEffect(() => {
    if (!!modelError) {
      handleClearAllView();
    }
  }, [modelError]);

  return (
    <StyledProjectFileViewer data-component-name="ProjectFileViewer">
      <div className="projectFileViewer__container">
        {useMemo(() => {
          return (
            <>
              {!modelError && hasViewer ? (
                <div className="projectFileViewer__container_item viewer">
                  <div className="projectFileViewer__viewer_box">
                    <ProjectModelViewer
                      model={clinicViewModelFiles.value}
                      isModelFileLoadingProgress={isModelFileLoadingProgress}
                      width={1157}
                      height={710}
                      className={cx('projectFileViewer__modelViewer', {
                        on: clinicViewModelFiles.value.length > 0,
                      })}
                      onLoading={isLoading => isModelFileLoadingProgress.setValue(isLoading)}
                    />

                    <div
                      className={cx('projectFileViewer__htmlViewer', {
                        on: !!clinicViewHtmlFiles.value,
                      })}
                    >
                      <iframe
                        src={clinicViewHtmlFiles.value?.htmlUrl}
                        frameBorder="0"
                        title="3D model web viewer"
                      ></iframe>
                    </div>

                    <div
                      className={cx('projectFileViewer__imageViewer', {
                        on: !!clinicViewImageFiles.value,
                      })}
                    >
                      <img src={clinicViewImageFiles.value?.imgUrl} />
                    </div>
                  </div>
                </div>
              ) : modelError ? (
                <div className="projectFileViewer__viewer_error_text">
                  <CustomText
                    style={{
                      width: '100%',
                      paddingLeft: 20,
                      paddingRight: 20,
                      textAlign: 'center',
                    }}
                    fontSize={16}
                    fontColor={color.gray_week_font}
                  >
                    <T>MODEL_LOADER_ERROR</T>
                  </CustomText>
                  <CustomText
                    style={{
                      width: '100%',
                      paddingLeft: 20,
                      paddingRight: 20,
                      textAlign: 'center',
                    }}
                    fontSize={14}
                    fontColor={color.gray_week_font}
                  >
                    {modelError}
                  </CustomText>
                </div>
              ) : (
                <div className="projectFileViewer__viewer_help_text">
                  <T>PROJECT_SCREEN_DISPLAYED</T>
                </div>
              )}
            </>
          );
        }, [
          modelError,
          hasViewer,
          clinicViewModelFiles.value,
          clinicViewImageFiles.value,
          clinicViewHtmlFiles.value,
        ])}
      </div>
    </StyledProjectFileViewer>
  );
}

export default React.memo(ProjectFileViewer);

const StyledProjectFileViewer = styled.div<{}>`
  width: 1157px;
  height: 710px;
  border-radius: 0 0 10px 0;
  overflow: hidden;
  position: relative;
  .projectFileViewer__viewer_box {
    position: relative;
    width: 1157px;
    height: 710px;

    background-color: #fbfbfb;
    overflow: hidden;
  }
  .projectFileViewer__viewer_help_text {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 9;
    width: 100%;
    height: 710px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d1d1d1;
    font-size: 16px;
  }
  .projectFileViewer__viewer_error_text {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 9;
    width: 100%;
    height: 710px;
    row-gap: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .projectFileViewer__modelViewer {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    visibility: hidden;
    &.on {
      z-index: 10;
      visibility: visible;
    }
  }
  .projectFileViewer__htmlViewer {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    overflow: overlay;
    visibility: hidden;
    &.on {
      z-index: 10;
      visibility: visible;
    }
    &,
    iframe {
      width: 1157px;
      height: 710px;
    }
    iframe {
    }
  }
  .projectFileViewer__imageViewer {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    overflow: overlay;
    visibility: hidden;
    &.on {
      z-index: 10;
      visibility: visible;
    }
    img {
      width: 1157px;
      height: 710px;
    }
  }

  .projectFileViewer__select_partner_box {
    padding: 30px;
    border: 1px solid #e1e1e1;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .projectFileViewer__select_partner_label {
      font-size: 14px;
      font-weight: 500;
      color: #303030;
    }
    .projectFileViewer__select_partner_name {
      width: 270px;
      .projectFileViewer__select_partner {
        &.fake_box {
          width: 100%;
          height: 32px;
          border: 1px solid #e1e1e1;
          display: flex;
          align-items: center;
          padding: 0 8px;
          padding-right: 55px;
          font-size: 12px;
          border-radius: 5px;
        }
      }
    }
  }
`;
